import type { User } from "@supabase/supabase-js";

/**
 * Privileged app roles, set server-side on the Supabase auth user via
 * `app_metadata.role` (dashboard → Auth → user → App Metadata, or the admin
 * API). `app_metadata` is server-only-writable and rides in the JWT, so no
 * schema migration or client bundle change is needed to grant/revoke.
 *
 *   - "tester" — full ungated curriculum (flips the `unlockEverything` pref).
 *   - "qa"     — everything a tester gets, plus the web Persona switcher in
 *                production.
 *
 * See docs/ops/manage-test-users.md.
 */
export type AppRole = "tester" | "qa";

export function appRole(user: Pick<User, "app_metadata"> | null | undefined): AppRole | null {
  if (!user) return null;
  const role = (user.app_metadata as { role?: unknown } | undefined)?.role;
  return role === "tester" || role === "qa" ? role : null;
}

/** True for any privileged role — the whole curriculum is unlocked (both platforms). */
export function hasCurriculumUnlock(user: Pick<User, "app_metadata"> | null | undefined): boolean {
  return appRole(user) !== null;
}

/** True only for QA — adds the web Persona switcher in production. */
export function isQA(user: Pick<User, "app_metadata"> | null | undefined): boolean {
  return appRole(user) === "qa";
}
